import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { compose } from 'redux'
import * as R from 'ramda'
import { withRouter } from 'react-router-dom'
import { TEMP_ID_PREFIX } from 'organisms/SurveySettingsV2/constants'

import { Loader } from 'shared/components/Loader'
import { connectApi } from 'libs/connectApi'
import { SurveySettingFormWithMetricReorderFlag as SurveySettingForm } from './SurveySettingForm'
import constants from 'config/constants'
import {
  generateQuestionRequest,
  generateFollowUpQuestionsRequest,
  generateSliderChoicesRequest,
  generateChoicesRequest,
  transformResponseToPresentationalState,
  getQuestionTemplate,
  generateCsatChoicesRequest,
  createDefaultChoicesResponse,
  generateRatingScaleMinmaxChoicesRequest,
  generateStarChoicesRequest,
  generateVisibilityRequest,
  generateDeleteVisibilityRequest,
  hasActiveVisibilityRule,
  findMetricQuestion,
  isMetricQuestion,
} from './utils'
import { toast } from 'libs/toast'
import { withSurveySettingContext } from 'pages/SurveySettings/context'
import generateUserActivityLog from 'libs/generateUserActivityLog'
import { ACTION_NAME } from 'hooks/useSaveUserActivityLog'
import ChooseSurveyTypeModalV2 from 'components/Admin/Surveys/ChooseSurveyTypeModalV2'
import useFeatureToggle from 'hooks/useFeatureToggle'

export class SurveySettingsV2Container extends Component {
  state = {
    isQuestionCreated: false,
  }

  convertQuestionAndFollowup = (question, questionIndex, action) => {
    let requestData = []
    requestData.push(generateQuestionRequest(question, questionIndex, action))

    // PET-1965: follow-up generation belongs to the metric question, which can
    // sit at any index once the metric-reorder flag is on. Positional check kept
    // for the flag-off path (index 0 is the metric there by construction).
    const isPrimary = this.props.enableMetricReorder
      ? isMetricQuestion(question)
      : questionIndex === 0
    if (isPrimary) {
      const isEnableFollowUp = R.prop('displayFollowUpQuestion', question)
      if (isEnableFollowUp) {
        const followUpQuestions = generateFollowUpQuestionsRequest(question, questionIndex, action)
        requestData = requestData.concat(followUpQuestions)
      }
    }
    return requestData
  }

  convertCsatChoices(question, questionIndex) {
    const choicesResponse = R.prop('choices', this.getQuestionResponse(question))
    const defaultChoiceResponse = createDefaultChoicesResponse(
      R.path(['questions', 0, 'choices'], this.getSurveyQuestions())
    )

    return generateCsatChoicesRequest(
      choicesResponse || defaultChoiceResponse,
      question,
      questionIndex
    )
  }

  convertChoices = (question, questionIndex, action) => {
    if (question.type === constants.questionType.slider) {
      return generateSliderChoicesRequest(question, questionIndex, action)
    } else if (question.type === constants.questionType.multipleChoice) {
      return generateChoicesRequest(question, questionIndex)
    } else if (question.type === constants.questionType.multi_select_choice) {
      return generateChoicesRequest(question, questionIndex)
    } else if (question.type === constants.questionType.rating_scale_minmax) {
      return generateRatingScaleMinmaxChoicesRequest(question, questionIndex, action)
    } else if (question.type === constants.questionType.star) {
      return generateStarChoicesRequest(question, questionIndex, action)
    }

    return []
  }
  getSavedMetricPosition = () => {
    const savedQuestions = (this.props.survey?.data && this.props.survey.data.questions) || []
    if (savedQuestions.length === 0) {
      // New surveys start from the metric-first template, so a screener-first
      // create diffs against position 1 and still emits the AC12 events.
      return 1
    }
    const savedMetricQuestion = findMetricQuestion(savedQuestions)
    const savedMetricIndex = savedMetricQuestion ? savedQuestions.indexOf(savedMetricQuestion) : -1
    return savedMetricIndex >= 0 ? savedMetricIndex + 1 : null
  }

  sendingDeleteQuestion = ({ question, questionIndex, onFailure = () => {} }) => {
    const action = 'delete'
    // PET-1965 AC12: single-question deletes don't move the metric — clear any
    // stale shadow from a prior form save so the telemetry falls back to the
    // snapshot position (changed=false).
    this.metricPositionAtSave = undefined
    this.metricPositionBeforeSave = this.getSavedMetricPosition()
    const requestData = this.convertDeleteQuestionToRequestPattern(question, questionIndex, action)

    this.props.deleteQuestion
      .callApi({
        data: requestData,
      })
      .then(() => {
        toast.success('Question deleted successfully.')
        this.saveUserActivityLog({ ...requestData }, action, 200)
      })
      .catch((e) => {
        console.error(e)
        toast.error('Oops, there was an error saving your survey. Please try again.')
        this.saveUserActivityLog({ ...requestData }, action, e.status)
        onFailure()
      })
  }

  sendingForm = ({ questions }) => {
    let requestData = []

    const deletedVisibility = this.props.context.deletedVisibility
    const deletedVisibilityIds = R.map(R.prop('id'), deletedVisibility)

    deletedVisibility.map((visibility) => {
      const action = 'delete'
      requestData = requestData.concat(generateDeleteVisibilityRequest(visibility, action))
    })

    // PET-1965 AC12: shadow the metric position of the form state being saved,
    // before requestData is built, so saveUserActivityLog can diff it against
    // the last saved snapshot (`props.survey.data.questions`).
    const metricQuestionAtSave = findMetricQuestion(questions)
    const metricIndexAtSave = metricQuestionAtSave ? questions.indexOf(metricQuestionAtSave) : -1
    this.metricPositionAtSave = metricIndexAtSave >= 0 ? metricIndexAtSave + 1 : null
    this.metricPositionBeforeSave = this.getSavedMetricPosition()
    questions.map((question, questionIndex) => {
      const isNewQuestion = (question) => {
        if (R.prop('id', question)) {
          return `${question.id}`.startsWith(TEMP_ID_PREFIX)
        } else {
          return true
        }
      }

      const action = isNewQuestion(question) ? 'create' : 'update'

      requestData = requestData.concat(
        this.convertQuestionAndChoiceToRequestPattern(question, questionIndex, action)
      )
      if (!R.isEmpty(question.visibilityCondition)) {
        const visibilityCondition = R.prop('visibilityCondition', question) || []

        const activeVisibility = visibilityCondition.filter(
          (visibility) => !R.includes(visibility.id, deletedVisibilityIds)
        )

        activeVisibility.map((visibility) => {
          if (R.isEmpty(visibility)) {
            return
          }

          if (!hasActiveVisibilityRule(visibility)) {
            if (visibility.id && visibility.id !== 'temp_visibility_id') {
              requestData = requestData.concat(
                generateDeleteVisibilityRequest(visibility, 'delete')
              )
            }
            return
          }

          const action = visibility.id === 'temp_visibility_id' ? 'create' : 'update'
          requestData = requestData.concat(generateVisibilityRequest(visibility, action))
        })
      }
    })

    const isAnyChoiceIsUndefined = R.any(R.isEmpty)(
      R.map(
        R.path(['data', 'value']),
        requestData.filter((each) => each.type === 'choice')
      )
    )
    if (isAnyChoiceIsUndefined) {
      toast.error('Please provide text labels for every question choice.')
    } else if (
      notAllCSATQuestionChoicesContainImage(findMetricQuestion(questions) || questions[0])
    ) {
      toast.error(
        'Whoops! Not all choices contain an image. Please use a smaller rating scale or contact Simplesat support to upload more icons.'
      )
    } else {
      const emptyQuestion = R.isEmpty(R.prop('questions', this.props.survey.data))
      const action = emptyQuestion ? 'create' : 'update'

      this.props.createQuestions
        .callApi({
          data: requestData,
          update: (store, survey) => {
            const url = `dashboard/survey-builder/v2/${this.props.surveyToken}/`
            store.write(url, survey)
          },
        })
        .then(() => {
          const showSuccessfullUpdateToast = action === 'update'
          showSuccessfullUpdateToast && toast.success('Your survey has been updated.')
          const surveySettingContext = this.props.context
          surveySettingContext.setContext({
            isSurveyBuilderDirty: false,
            deletedVisibility: [],
          })

          this.saveUserActivityLog({ ...requestData }, action, 200)
        })
        .catch((e) => {
          console.error(e)
          toast.error('Oops, there was an error saving your survey. Please try again.')
          this.saveUserActivityLog({ ...requestData }, action, e.status)
        })
    }
  }

  /**
   *
   * @param {object} requestBody
   * @param {number} statusCode
   * @param {string} action
   */
  saveUserActivityLog = (requestBody, action, statusCode) => {
    const actionName = action === 'create' ? ACTION_NAME.created_survey : ACTION_NAME.updated_survey
    // PET-1965 telemetry: annotate the payload with the metric-position signals
    // the gateway fans out into dedicated Mixpanel events (metric_position_changed
    // + no_rating_feedback_enabled). The gateway derives those events from the
    // position fields alone, so no adoption boolean is sent from here.
    // Gate under `enableMetricReorder` so pre-flag surveys don't emit noisy
    // fields — AC13 requires the annotation to obey the same flag that gates
    // the feature.
    let telemetryExtras = {}
    // Failed saves skip the extras — the position never persisted, and the
    // gateway fan-out has no status guard, so it would count a failed reorder
    // as adoption.
    const isSavePersisted = statusCode >= 200 && statusCode < 300
    if (this.props.enableMetricReorder && isSavePersisted) {
      // AC12: `metric_position_previous` must be the shadow captured BEFORE the
      // save call — connectApi's `update` writes the saved survey into the store
      // before this runs, so reading props here yields previous === current.
      const metricPositionPrevious =
        this.metricPositionBeforeSave !== undefined
          ? this.metricPositionBeforeSave
          : this.getSavedMetricPosition()
      const metricPositionCurrent =
        this.metricPositionAtSave !== undefined ? this.metricPositionAtSave : metricPositionPrevious
      telemetryExtras = {
        metric_position_current: metricPositionCurrent,
        metric_position_previous: metricPositionPrevious,
        metric_position_changed:
          metricPositionPrevious !== null && metricPositionCurrent !== metricPositionPrevious,
      }
    }
    const data = generateUserActivityLog({
      action: actionName,
      target: `sur_${this.props.surveyId}`,
      statusCode,
      requestBody: {
        ...requestBody,
        ...telemetryExtras,
      },
    })
    this.props.createUserActivityLog.callApi({ data })
  }

  getSurveyQuestions = () => {
    if (this.shouldInitializeSurvey()) {
      const metric = this.props.location.state.template
      const companyName = R.propOr('The company', 'name', this.props.account.data)
      const initialValues = {
        questions: [getQuestionTemplate(metric, companyName)],
      }
      return initialValues
    }

    return { questions: transformResponseToPresentationalState(this.props.survey.data) }
  }

  convertQuestionAndChoiceToRequestPattern = (question, questionIndex, action) => {
    const isCsatQuestion = question.metric === constants.questionMetricType.csat
    const questionRequest = this.convertQuestionAndFollowup(question, questionIndex, action)

    const choicesRequest = isCsatQuestion
      ? this.convertCsatChoices(question, questionIndex)
      : this.convertChoices(question, questionIndex, action)

    return [].concat(questionRequest, choicesRequest)
  }

  convertDeleteQuestionToRequestPattern = (question, questionIndex, action) => {
    return this.convertQuestionAndFollowup(question, questionIndex, action)
  }

  getQuestionResponse(question) {
    return this.props.survey.data.questions.find((item) => item.id === question.id)
  }

  componentDidUpdate(prevProps) {
    const surveySettingContext = this.props.context
    if (prevProps.createQuestions.loading !== this.props.createQuestions.loading) {
      surveySettingContext.setContext({
        isSubmitting: this.props.createQuestions.loading,
      })
    }

    if (
      !shallowCompare(
        [
          prevProps.survey.data,
          prevProps.choiceIconSetOptions.data,
          prevProps.account.data,
          prevProps.location.state,
        ],
        [
          this.props.survey.data,
          this.props.choiceIconSetOptions.data,
          this.props.account.data,
          this.props.location.state,
        ]
      )
    ) {
      if (
        this.props.survey.data &&
        this.props.choiceIconSetOptions.data &&
        this.props.account.data &&
        this.shouldInitializeSurvey()
      ) {
        const { template: metric } = this.props.location.state || {}
        const companyName = this.props.account.data.name || 'The company'
        const primaryQuestion = getQuestionTemplate(metric, companyName)

        const shouldAddChoiceIconSet =
          metric === constants.questionMetricType.csat ||
          metric === constants.questionMetricType.star
        if (shouldAddChoiceIconSet) {
          const defaultName = {
            CSAT: 'smiley',
            '5-STAR': 'defaultStar',
          }
          const defaultChoiceIconSet = R.find(R.propEq('name', defaultName[metric]))(
            this.props.choiceIconSetOptions.data
          )
          primaryQuestion.choiceIconSet = defaultChoiceIconSet || null
        }

        const initialQuestion = {
          questions: [primaryQuestion],
        }

        if (!this.state.isQuestionCreated) {
          this.setState({ isQuestionCreated: true }, () => {
            this.sendingForm(initialQuestion)
          })
        }
      }
    }
  }

  shouldInitializeSurvey() {
    const hasNoQuestions = R.isEmpty(this.props.survey.data.questions)
    const hasTemplateMetric = R.prop('template', this.props.location.state)

    return hasNoQuestions && hasTemplateMetric
  }

  shouldShowMetricSelectionModal() {
    const hasNoQuestions = R.isEmpty(this.props.survey.data.questions)
    const hasNoTemplateMetric = !R.prop('template', this.props.location.state)

    return hasNoQuestions && hasNoTemplateMetric
  }

  render() {
    const { survey, choiceIconSetOptions, account } = this.props
    const hasTemplateFromLocationState = R.pipe(
      R.path(['state', 'template']),
      Boolean
    )(this.props.location)

    const loading = !survey.data || !choiceIconSetOptions.data || !account.data

    if (loading) {
      return (
        <div style={{ position: 'relative', minHeight: 200 }}>
          <Loader />
        </div>
      )
    }

    const workspaceToken = R.path(['params', 'workspaceToken'], this.props.match)

    if (this.shouldShowMetricSelectionModal()) {
      // show modal prompting users to input tempalte, this happens
      // when users fail to create the survey completely the first time.
      const handleSelectMetric = (metric) => {
        this.props.history.push(
          `/${workspaceToken}/settings/surveys/${this.props.surveyId}/settings/`,
          {
            template: metric,
          }
        )
      }

      const handleClose = () => {
        this.props.history.push(`/${workspaceToken}/settings/surveys/`)
      }

      return (
        <ChooseSurveyTypeModalV2
          handleClose={handleClose}
          isOpenModal
          handleOnSelectCSAT={() => handleSelectMetric('CSAT')}
          handleOnSelectNPS={() => handleSelectMetric('NPS')}
          handleOnSelectCES={() => handleSelectMetric('CES')}
          handleOnSelectFiveStar={() => handleSelectMetric('5-STAR')}
        />
      )
    }

    return (
      <SurveySettingForm
        surveyToken={this.props.surveyToken}
        surveyQuestions={this.getSurveyQuestions()}
        questionsResponse={this.props.survey.data}
        sendingForm={this.sendingForm}
        sendingDeleteQuestion={this.sendingDeleteQuestion}
        hasTemplateFromLocationState={hasTemplateFromLocationState}
        isSubmitting={this.props.createQuestions.loading}
        choiceIconSetOptions={choiceIconSetOptions.data}
      />
    )
  }
}

function notAllCSATQuestionChoicesContainImage(primaryQuestion) {
  if (primaryQuestion.metric === constants.questionMetricType.csat) {
    return notAllPrimaryQuestionChoicesContainImage(primaryQuestion)
  } else {
    return false
  }
}

function notAllPrimaryQuestionChoicesContainImage(primaryQuestion) {
  const ratingScale = primaryQuestion.choices.length
  const choiceImages = getChoiceImages(primaryQuestion.choiceIconSet, ratingScale)
  return choiceImages.some(R.isNil)
}

function getChoiceImages(choiceIconSet, ratingScale) {
  const SCALE_RATING = {
    2: [2, 4],
    3: [2, 3, 4],
    4: [2, 3, 4, 5],
    5: [1, 2, 3, 4, 5],
  }

  return SCALE_RATING[ratingScale].map((ratingValue) => {
    return choiceIconSet['choice_value_' + ratingValue]
  })
}

SurveySettingsV2Container.propTypes = {
  survey: PropTypes.object,
  surveyId: PropTypes.number,
  createQuestions: PropTypes.shape({
    callApi: PropTypes.func,
    loading: PropTypes.bool,
  }),
  surveyToken: PropTypes.string,
  location: PropTypes.shape({
    state: PropTypes.object,
  }),
  context: PropTypes.object,
  createUserActivityLog: PropTypes.shape({
    callApi: PropTypes.func,
    loading: PropTypes.bool,
  }),
}

const SurveySettingsV2Composed = compose(
  connectApi({
    name: 'account',
    url: ({ companyId }) => `account/company/${companyId}/`,
  }),
  connectApi({
    name: 'survey',
    url: ({ surveyToken }) => `dashboard/survey-builder/v2/${surveyToken}/`,
    schema: '@surveyBuilder',
  }),
  connectApi({
    name: 'deleteQuestion',
    url: ({ surveyToken }) => `dashboard/survey-builder/v2/${surveyToken}/`,
    method: 'post',
    skip: true,
  }),
  connectApi({
    name: 'createQuestions',
    url: ({ surveyToken }) => `dashboard/survey-builder/v2/${surveyToken}/`,
    method: 'post',
    skip: true,
    schema: '@surveyBuilder',
  }),
  connectApi({
    name: 'choiceIconSetOptions',
    url: 'questions/choice-icon-sets/',
  }),
  connectApi({
    name: 'createUserActivityLog',
    url: `/user-activity-log/write/`,
    method: 'post',
    skip: true,
  }),

  withRouter,
  withSurveySettingContext
)(SurveySettingsV2Container)

// Functional shim: pull the metric-reorder flag from `useFeatureToggle` and
// forward it as a prop. AC13 requires `saveUserActivityLog` to gate its
// telemetry extras under the same flag the survey builder UI uses, but the class
// component sits behind a `compose(...)` stack of `connectApi` HOCs and can't
// consume hooks directly. This mirrors the `SurveySettingFormWithMetricReorderFlag`
// wrapper in `./SurveySettingForm.js`.
export const SurveySettingsV2 = (props) => {
  const { enableMetricReorder } = useFeatureToggle()
  return <SurveySettingsV2Composed {...props} enableMetricReorder={enableMetricReorder} />
}

function shallowCompare(previousValues, values) {
  return previousValues.every((previousValue, index) => {
    const value = values[index]
    return previousValue === value
  })
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/organisms/SurveySettingsV2/index.js